import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Shield, Users, Plus, Edit, Check, X, Loader2, UserPlus, 
  Eye, Trash2, Settings, ChevronDown, ChevronUp, Lock, Unlock,
  Crown, Search, UserCog, Store, ShoppingCart, Save, Database
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { admin } from '@/lib/api';
import { toast } from 'sonner';
import { formatDate } from '@/lib/utils';

const RoleManagementTab = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [staff, setStaff] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [myPermissions, setMyPermissions] = useState(null);
  
  // All users for super admin
  const [allUsers, setAllUsers] = useState([]);
  const [userFilter, setUserFilter] = useState('all');
  const [userSearch, setUserSearch] = useState('');
  const [showChangeRoleDialog, setShowChangeRoleDialog] = useState(false);
  const [selectedUserForRole, setSelectedUserForRole] = useState(null);
  const [newUserRole, setNewUserRole] = useState('');
  const [newUserAdminRole, setNewUserAdminRole] = useState('admin');
  const [customPermissions, setCustomPermissions] = useState([]);
  
  // Dialog states
  const [showAddStaffDialog, setShowAddStaffDialog] = useState(false);
  const [showPermissionsDialog, setShowPermissionsDialog] = useState(false);
  const [selectedStaffMember, setSelectedStaffMember] = useState(null);
  const [editingStaffId, setEditingStaffId] = useState(null);
  const [selectedRole, setSelectedRole] = useState('');
  const [saving, setSaving] = useState(false);
  const [backingUp, setBackingUp] = useState(false);
  
  // Expanded role sections
  const [expandedRoles, setExpandedRoles] = useState({});
  
  // New staff form
  const [newStaffData, setNewStaffData] = useState({
    name: '',
    email: '',
    password: '',
    admin_role: 'support'
  });

  useEffect(() => {
    fetchData();
  }, []);

  // Super Admin: Backup admin users
  const handleBackupAdmins = async () => {
    setBackingUp(true);
    try {
      const response = await admin.backupAdmins();
      toast.success(`${response.data.admins_count} admin kullanıcısı yedeklendi`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Yedekleme başarısız');
    } finally {
      setBackingUp(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [rolesRes, staffRes, myPermsRes] = await Promise.all([
        admin.getRoles(),
        admin.getStaff().catch(() => ({ data: { staff: [] } })),
        admin.getMyPermissions()
      ]);
      
      setRoles(rolesRes.data.roles || []);
      setPermissions(rolesRes.data.permissions || []);
      setCanManage(rolesRes.data.can_manage || false);
      setStaff(staffRes.data.staff || []);
      setMyPermissions(myPermsRes.data);
      
      // If super admin, fetch all users
      if (myPermsRes.data?.is_super_admin) {
        try {
          const usersRes = await admin.getUsers({ limit: 500 });
          setAllUsers(usersRes.data.users || []);
        } catch (e) {
          console.error('Failed to fetch users:', e);
        }
      }
    } catch (error) {
      console.error('Failed to fetch role data:', error);
      toast.error(t('admin.roles.loadError') || 'Rol bilgileri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRole = async (userId) => {
    if (!selectedRole) return;
    
    setSaving(true);
    try {
      await admin.updateStaffRole(userId, selectedRole);
      toast.success(t('admin.roles.roleUpdated') || 'Rol güncellendi');
      setEditingStaffId(null);
      setSelectedRole('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('admin.roles.updateError') || 'Rol güncellenemedi');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateStaff = async () => {
    if (!newStaffData.name || !newStaffData.email || !newStaffData.password) {
      toast.error(t('admin.roles.fillAllFields') || 'Tüm alanları doldurun');
      return;
    }
    
    setSaving(true);
    try {
      await admin.createStaffMember(newStaffData);
      toast.success(t('admin.roles.staffCreated') || 'Personel oluşturuldu');
      setShowAddStaffDialog(false);
      setNewStaffData({ name: '', email: '', password: '', admin_role: 'support' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('admin.roles.createError') || 'Personel oluşturulamadı');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveStaff = async (userId) => {
    if (!confirm('Bu personeli silmek istediğinizden emin misiniz?')) return;
    
    try {
      await admin.removeStaffMember(userId);
      toast.success('Personel silindi');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Personel silinemedi');
    }
  };

  // Super Admin: Change any user's role
  const handleChangeUserRole = async () => {
    if (!selectedUserForRole || !newUserRole) return;
    
    setSaving(true);
    try {
      // Determine which permissions to send
      const permissionsToSend = newUserAdminRole === 'custom' ? customPermissions : null;
      
      await admin.changeUserRole(
        selectedUserForRole.id, 
        newUserRole, 
        newUserRole === 'admin' ? newUserAdminRole : null,
        permissionsToSend
      );
      toast.success(`Kullanıcı rolü '${newUserRole}' olarak güncellendi`);
      setShowChangeRoleDialog(false);
      setSelectedUserForRole(null);
      setNewUserRole('');
      setCustomPermissions([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Rol güncellenemedi');
    } finally {
      setSaving(false);
    }
  };

  // Filter users based on search and filter
  const filteredUsers = allUsers.filter(user => {
    const matchesSearch = !userSearch || 
      user.name?.toLowerCase().includes(userSearch.toLowerCase()) ||
      user.email?.toLowerCase().includes(userSearch.toLowerCase());
    
    const matchesFilter = userFilter === 'all' || user.role === userFilter;
    
    return matchesSearch && matchesFilter;
  });

  const handleViewPermissions = (member) => {
    setSelectedStaffMember(member);
    setShowPermissionsDialog(true);
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'super_admin': return 'bg-red-100 text-red-700 border-red-300';
      case 'admin': return 'bg-purple-100 text-purple-700 border-purple-300';
      case 'moderator': return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'support': return 'bg-green-100 text-green-700 border-green-300';
      case 'finance': return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'super_admin': return <Shield className="w-4 h-4 text-red-600" />;
      case 'admin': return <Settings className="w-4 h-4 text-purple-600" />;
      case 'moderator': return <Eye className="w-4 h-4 text-blue-600" />;
      case 'support': return <Users className="w-4 h-4 text-green-600" />;
      case 'finance': return <Lock className="w-4 h-4 text-yellow-600" />;
      default: return <Users className="w-4 h-4" />;
    }
  };

  const toggleRoleExpand = (roleKey) => {
    setExpandedRoles(prev => ({
      ...prev,
      [roleKey]: !prev[roleKey]
    }));
  };

  // Group permissions by category
  const groupPermissions = (perms) => {
    const groups = {};
    perms.forEach(perm => {
      const [category] = perm.split('.');
      if (!groups[category]) groups[category] = [];
      groups[category].push(perm);
    });
    return groups;
  };

  const getPermissionLabel = (perm) => {
    const labels = {
      'users.view': 'Kullanıcıları Görüntüle',
      'users.edit': 'Kullanıcıları Düzenle',
      'users.delete': 'Kullanıcıları Sil',
      'sellers.view': 'Satıcıları Görüntüle',
      'sellers.approve': 'Satıcıları Onayla',
      'sellers.edit': 'Satıcıları Düzenle',
      'products.view': 'Ürünleri Görüntüle',
      'products.edit': 'Ürünleri Düzenle',
      'products.delete': 'Ürünleri Sil',
      'orders.view': 'Siparişleri Görüntüle',
      'orders.manage': 'Siparişleri Yönet',
      'reports.view': 'Raporları Görüntüle',
      'reports.export': 'Raporları Dışa Aktar',
      'settings.view': 'Ayarları Görüntüle',
      'settings.edit': 'Ayarları Düzenle',
      'finance.view': 'Finans Görüntüle',
      'finance.manage': 'Finans Yönet',
      'support.view': 'Destek Görüntüle',
      'support.respond': 'Destek Yanıtla',
      '*': 'Tam Yetki (Tüm İşlemler)'
    };
    return labels[perm] || perm;
  };

  const getCategoryLabel = (category) => {
    const labels = {
      'users': '👥 Kullanıcılar',
      'sellers': '🏪 Satıcılar',
      'products': '📦 Ürünler',
      'orders': '📋 Siparişler',
      'reports': '📊 Raporlar',
      'settings': '⚙️ Ayarlar',
      'finance': '💰 Finans',
      'support': '🎧 Destek',
      '*': '🔐 Tam Yetki'
    };
    return labels[category] || category;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* My Permissions Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            {t('admin.roles.myRole') || 'Benim Rolüm ve Yetkilerim'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 mb-4">
            <Badge className={`${getRoleBadgeColor(myPermissions?.admin_role)} px-3 py-1 text-sm flex items-center gap-2`}>
              {getRoleIcon(myPermissions?.admin_role)}
              {myPermissions?.role_name || 'Admin'}
            </Badge>
            {myPermissions?.is_super_admin && (
              <Badge variant="destructive" className="px-3 py-1">
                <Shield className="w-3 h-3 mr-1" />
                Super Admin
              </Badge>
            )}
          </div>
          
          {/* My Permissions List */}
          {myPermissions?.permissions && (
            <div className="mt-4 p-4 bg-muted/50 rounded-lg">
              <p className="text-sm font-medium mb-2">Yetkilerim:</p>
              <div className="flex flex-wrap gap-2">
                {myPermissions.permissions.includes('*') ? (
                  <Badge variant="secondary" className="bg-red-100 text-red-700">
                    <Unlock className="w-3 h-3 mr-1" />
                    Tam Yetki (Tüm İşlemler)
                  </Badge>
                ) : (
                  myPermissions.permissions.map((perm, idx) => (
                    <Badge key={idx} variant="outline" className="text-xs">
                      {getPermissionLabel(perm)}
                    </Badge>
                  ))
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Tabs defaultValue="roles" className="w-full">
        <TabsList className={`grid w-full ${myPermissions?.is_super_admin ? 'grid-cols-3' : 'grid-cols-2'}`}>
          <TabsTrigger value="roles">
            <Shield className="w-4 h-4 mr-2" />
            Roller ve Yetkiler
          </TabsTrigger>
          <TabsTrigger value="staff">
            <Users className="w-4 h-4 mr-2" />
            Personel Yönetimi
          </TabsTrigger>
          {myPermissions?.is_super_admin && (
            <TabsTrigger value="all-users">
              <Crown className="w-4 h-4 mr-2" />
              Tüm Kullanıcı Rolleri
            </TabsTrigger>
          )}
        </TabsList>

        {/* Roles & Permissions Tab */}
        <TabsContent value="roles" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Sistemdeki Roller ve Yetkileri</CardTitle>
              <CardDescription>Her rolün sahip olduğu yetkileri görüntüleyin</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {roles.map((role) => (
                  <Collapsible 
                    key={role.key}
                    open={expandedRoles[role.key]}
                    onOpenChange={() => toggleRoleExpand(role.key)}
                  >
                    <div className={`border-2 rounded-lg ${getRoleBadgeColor(role.key)}`}>
                      <CollapsibleTrigger asChild>
                        <div className="p-4 cursor-pointer flex items-center justify-between hover:bg-black/5 transition-colors">
                          <div className="flex items-center gap-3">
                            {getRoleIcon(role.key)}
                            <div>
                              <h4 className="font-semibold">{role.name}</h4>
                              <p className="text-sm opacity-80">{role.description}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge variant="secondary">
                              {role.permissions?.includes('*') ? 'Tam Yetki' : `${role.permissions?.length || 0} Yetki`}
                            </Badge>
                            {expandedRoles[role.key] ? (
                              <ChevronUp className="w-5 h-5" />
                            ) : (
                              <ChevronDown className="w-5 h-5" />
                            )}
                          </div>
                        </div>
                      </CollapsibleTrigger>
                      
                      <CollapsibleContent>
                        <div className="px-4 pb-4 border-t border-current/20 pt-4">
                          <p className="text-sm font-medium mb-3">Bu Rolün Yetkileri:</p>
                          {role.permissions?.includes('*') ? (
                            <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                              <div className="flex items-center gap-2 text-red-700">
                                <Unlock className="w-5 h-5" />
                                <span className="font-semibold">Tam Yetki</span>
                              </div>
                              <p className="text-sm text-red-600 mt-1">
                                Bu rol sistemdeki tüm işlemleri yapabilir.
                              </p>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {Object.entries(groupPermissions(role.permissions || [])).map(([category, perms]) => (
                                <div key={category} className="p-3 bg-white/50 rounded-lg">
                                  <p className="text-sm font-medium mb-2">{getCategoryLabel(category)}</p>
                                  <div className="flex flex-wrap gap-2">
                                    {perms.map((perm, idx) => (
                                      <Badge key={idx} variant="outline" className="text-xs bg-white">
                                        <Check className="w-3 h-3 mr-1 text-green-600" />
                                        {getPermissionLabel(perm)}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* All Permissions Reference */}
          <Card>
            <CardHeader>
              <CardTitle>Tüm Yetkiler Referansı</CardTitle>
              <CardDescription>Sistemde tanımlı tüm yetkiler</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Yetki Kodu</TableHead>
                    <TableHead>Açıklama</TableHead>
                    <TableHead>Bu Yetkiye Sahip Roller</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {permissions.map((perm, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <code className="bg-muted px-2 py-1 rounded text-sm">{perm}</code>
                      </TableCell>
                      <TableCell>{getPermissionLabel(perm)}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {roles
                            .filter(r => r.permissions?.includes('*') || r.permissions?.includes(perm))
                            .map(r => (
                              <Badge key={r.key} className={`${getRoleBadgeColor(r.key)} text-xs`}>
                                {r.name}
                              </Badge>
                            ))
                          }
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Staff Management Tab */}
        <TabsContent value="staff">
          {canManage ? (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      Personel Yönetimi
                    </CardTitle>
                    <CardDescription>Admin personel listesi ve rol atamaları</CardDescription>
                  </div>
                  <Button onClick={() => setShowAddStaffDialog(true)}>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Personel Ekle
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Kullanıcı</TableHead>
                      <TableHead>E-posta</TableHead>
                      <TableHead>Mevcut Rol</TableHead>
                      <TableHead>Yetkiler</TableHead>
                      <TableHead>Katılım</TableHead>
                      <TableHead className="text-right">İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {staff.map((member) => {
                      const memberRole = roles.find(r => r.key === member.admin_role);
                      const memberPerms = memberRole?.permissions || [];
                      
                      return (
                        <TableRow key={member.id}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                <span className="text-primary font-semibold">
                                  {member.name?.charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <div>
                                <p className="font-medium">{member.name}</p>
                                {member.id === myPermissions?.user_id && (
                                  <span className="text-xs text-muted-foreground">(Siz)</span>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-muted-foreground">{member.email}</TableCell>
                          <TableCell>
                            {editingStaffId === member.id ? (
                              <Select value={selectedRole} onValueChange={setSelectedRole}>
                                <SelectTrigger className="w-40">
                                  <SelectValue placeholder="Rol seçin" />
                                </SelectTrigger>
                                <SelectContent>
                                  {roles.map((role) => (
                                    <SelectItem key={role.key} value={role.key}>
                                      <div className="flex items-center gap-2">
                                        {getRoleIcon(role.key)}
                                        {role.name}
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <Badge className={`${getRoleBadgeColor(member.admin_role || 'admin')} flex items-center gap-1 w-fit`}>
                                {getRoleIcon(member.admin_role)}
                                {member.role_name || 'Admin'}
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleViewPermissions(member)}
                              className="text-xs"
                            >
                              <Eye className="w-3 h-3 mr-1" />
                              {memberPerms.includes('*') ? 'Tam Yetki' : `${memberPerms.length} Yetki`}
                            </Button>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {member.created_at ? formatDate(member.created_at) : '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            {editingStaffId === member.id ? (
                              <div className="flex items-center justify-end gap-2">
                                <Button 
                                  size="icon" 
                                  variant="ghost"
                                  onClick={() => handleUpdateRole(member.id)}
                                  disabled={saving || !selectedRole}
                                >
                                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 text-green-600" />}
                                </Button>
                                <Button 
                                  size="icon" 
                                  variant="ghost"
                                  onClick={() => { setEditingStaffId(null); setSelectedRole(''); }}
                                >
                                  <X className="w-4 h-4 text-red-600" />
                                </Button>
                              </div>
                            ) : (
                              <div className="flex items-center justify-end gap-1">
                                <Button 
                                  size="icon" 
                                  variant="ghost"
                                  onClick={() => { setEditingStaffId(member.id); setSelectedRole(member.admin_role || 'admin'); }}
                                  disabled={member.id === myPermissions?.user_id}
                                  title={member.id === myPermissions?.user_id ? 'Kendi rolünüzü değiştiremezsiniz' : 'Rol değiştir'}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                {member.id !== myPermissions?.user_id && (
                                  <Button 
                                    size="icon" 
                                    variant="ghost"
                                    onClick={() => handleRemoveStaff(member.id)}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    title="Personeli Sil"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Lock className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-semibold text-lg">Erişim Kısıtlı</h3>
                <p className="text-muted-foreground mt-2">
                  Personel yönetimi için Super Admin yetkisi gereklidir.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* All Users Role Management Tab (Super Admin Only) */}
        {myPermissions?.is_super_admin && (
          <TabsContent value="all-users" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Crown className="w-5 h-5 text-yellow-500" />
                      Tüm Kullanıcı Rolleri Yönetimi
                    </CardTitle>
                    <CardDescription>
                      Super Admin olarak tüm kullanıcıların rollerini değiştirebilirsiniz
                    </CardDescription>
                  </div>
                  <Button 
                    variant="outline" 
                    onClick={handleBackupAdmins}
                    disabled={backingUp}
                    className="bg-green-50 border-green-200 hover:bg-green-100 text-green-700"
                  >
                    {backingUp ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Database className="w-4 h-4 mr-2" />
                    )}
                    Admin Backup Güncelle
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Filters */}
                <div className="flex items-center gap-4 mb-6">
                  <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Kullanıcı ara..."
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  <Select value={userFilter} onValueChange={setUserFilter}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Rol filtrele" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tüm Roller</SelectItem>
                      <SelectItem value="admin">Adminler</SelectItem>
                      <SelectItem value="seller">Satıcılar</SelectItem>
                      <SelectItem value="buyer">Alıcılar</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* User Stats */}
                <div className="grid grid-cols-4 gap-4 mb-6">
                  <Card className="bg-purple-50 border-purple-200">
                    <CardContent className="p-4 text-center">
                      <Shield className="w-6 h-6 mx-auto text-purple-600 mb-2" />
                      <p className="text-2xl font-bold text-purple-700">
                        {allUsers.filter(u => u.role === 'admin').length}
                      </p>
                      <p className="text-sm text-purple-600">Adminler</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-green-50 border-green-200">
                    <CardContent className="p-4 text-center">
                      <Store className="w-6 h-6 mx-auto text-green-600 mb-2" />
                      <p className="text-2xl font-bold text-green-700">
                        {allUsers.filter(u => u.role === 'seller').length}
                      </p>
                      <p className="text-sm text-green-600">Satıcılar</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-blue-50 border-blue-200">
                    <CardContent className="p-4 text-center">
                      <ShoppingCart className="w-6 h-6 mx-auto text-blue-600 mb-2" />
                      <p className="text-2xl font-bold text-blue-700">
                        {allUsers.filter(u => u.role === 'buyer').length}
                      </p>
                      <p className="text-sm text-blue-600">Alıcılar</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-gray-50 border-gray-200">
                    <CardContent className="p-4 text-center">
                      <Users className="w-6 h-6 mx-auto text-gray-600 mb-2" />
                      <p className="text-2xl font-bold text-gray-700">
                        {allUsers.length}
                      </p>
                      <p className="text-sm text-gray-600">Toplam</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Users Table */}
                <ScrollArea className="h-[400px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Kullanıcı</TableHead>
                        <TableHead>E-posta</TableHead>
                        <TableHead>Mevcut Rol</TableHead>
                        <TableHead>Admin Rolü</TableHead>
                        <TableHead>Durum</TableHead>
                        <TableHead className="text-right">İşlemler</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredUsers.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                <span className="text-primary font-semibold">
                                  {user.name?.charAt(0).toUpperCase() || 'U'}
                                </span>
                              </div>
                              <div>
                                <p className="font-medium">{user.name || 'İsimsiz'}</p>
                                {user.id === myPermissions?.user_id && (
                                  <span className="text-xs text-muted-foreground">(Siz)</span>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">{user.email}</TableCell>
                          <TableCell>
                            <Badge className={
                              user.role === 'admin' ? 'bg-purple-100 text-purple-700' :
                              user.role === 'seller' ? 'bg-green-100 text-green-700' :
                              'bg-blue-100 text-blue-700'
                            }>
                              {user.role === 'admin' && <Shield className="w-3 h-3 mr-1" />}
                              {user.role === 'seller' && <Store className="w-3 h-3 mr-1" />}
                              {user.role === 'buyer' && <ShoppingCart className="w-3 h-3 mr-1" />}
                              {user.role === 'admin' ? 'Admin' : user.role === 'seller' ? 'Satıcı' : 'Alıcı'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {user.role === 'admin' && (
                              <Badge variant="outline" className={
                                user.admin_role === 'super_admin' ? 'border-red-300 text-red-700 bg-red-50' :
                                'border-gray-300'
                              }>
                                {user.admin_role === 'super_admin' && <Crown className="w-3 h-3 mr-1" />}
                                {user.admin_role === 'super_admin' ? 'Super Admin' :
                                 user.admin_role === 'admin' ? 'Admin' :
                                 user.admin_role === 'moderator' ? 'Moderatör' :
                                 user.admin_role === 'support' ? 'Destek' :
                                 user.admin_role === 'finance' ? 'Finans' :
                                 user.admin_role || 'Admin'}
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant={user.is_active !== false ? 'default' : 'destructive'}>
                              {user.is_active !== false ? 'Aktif' : 'Pasif'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            {user.id !== myPermissions?.user_id && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedUserForRole(user);
                                  setNewUserRole(user.role);
                                  setNewUserAdminRole(user.admin_role || 'admin');
                                  setShowChangeRoleDialog(true);
                                }}
                              >
                                <UserCog className="w-4 h-4 mr-1" />
                                Rol Değiştir
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Add Staff Dialog */}
      <Dialog open={showAddStaffDialog} onOpenChange={setShowAddStaffDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Yeni Personel Ekle</DialogTitle>
            <DialogDescription>
              Yeni bir admin personeli oluşturun ve rol atayın
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>İsim *</Label>
              <Input
                value={newStaffData.name}
                onChange={(e) => setNewStaffData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Personel adı"
              />
            </div>
            <div className="space-y-2">
              <Label>E-posta *</Label>
              <Input
                type="email"
                value={newStaffData.email}
                onChange={(e) => setNewStaffData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="ornek@turkishemarket.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Şifre *</Label>
              <Input
                type="password"
                value={newStaffData.password}
                onChange={(e) => setNewStaffData(prev => ({ ...prev, password: e.target.value }))}
                placeholder="••••••••"
              />
              <p className="text-xs text-muted-foreground">
                En az 8 karakter, bir büyük harf, bir küçük harf ve bir rakam
              </p>
            </div>
            <div className="space-y-2">
              <Label>Rol *</Label>
              <Select 
                value={newStaffData.admin_role} 
                onValueChange={(val) => setNewStaffData(prev => ({ ...prev, admin_role: val }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((role) => (
                    <SelectItem key={role.key} value={role.key}>
                      <div className="flex items-center gap-2">
                        {getRoleIcon(role.key)}
                        <span>{role.name}</span>
                        <span className="text-xs text-muted-foreground">
                          ({role.permissions?.includes('*') ? 'Tam Yetki' : `${role.permissions?.length} yetki`})
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Preview selected role permissions */}
            {newStaffData.admin_role && (
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-sm font-medium mb-2">Seçilen Rolün Yetkileri:</p>
                <div className="flex flex-wrap gap-1">
                  {roles.find(r => r.key === newStaffData.admin_role)?.permissions?.includes('*') ? (
                    <Badge variant="destructive" className="text-xs">Tam Yetki</Badge>
                  ) : (
                    roles.find(r => r.key === newStaffData.admin_role)?.permissions?.slice(0, 5).map((perm, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs">
                        {getPermissionLabel(perm)}
                      </Badge>
                    ))
                  )}
                  {(roles.find(r => r.key === newStaffData.admin_role)?.permissions?.length || 0) > 5 && (
                    <Badge variant="secondary" className="text-xs">
                      +{roles.find(r => r.key === newStaffData.admin_role).permissions.length - 5} daha
                    </Badge>
                  )}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddStaffDialog(false)}>
              İptal
            </Button>
            <Button onClick={handleCreateStaff} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Oluştur
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Permissions Dialog */}
      <Dialog open={showPermissionsDialog} onOpenChange={setShowPermissionsDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedStaffMember && getRoleIcon(selectedStaffMember.admin_role)}
              {selectedStaffMember?.name} - Yetkiler
            </DialogTitle>
            <DialogDescription>
              <Badge className={`${getRoleBadgeColor(selectedStaffMember?.admin_role)} mt-2`}>
                {selectedStaffMember?.role_name || 'Admin'}
              </Badge>
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="max-h-[400px] pr-4">
            <div className="space-y-4 py-4">
              {selectedStaffMember && (() => {
                const memberRole = roles.find(r => r.key === selectedStaffMember.admin_role);
                const memberPerms = memberRole?.permissions || [];
                
                if (memberPerms.includes('*')) {
                  return (
                    <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                      <div className="flex items-center gap-2 text-red-700">
                        <Unlock className="w-5 h-5" />
                        <span className="font-semibold">Tam Yetki</span>
                      </div>
                      <p className="text-sm text-red-600 mt-2">
                        Bu kullanıcı sistemdeki tüm işlemleri yapabilir.
                      </p>
                    </div>
                  );
                }
                
                const grouped = groupPermissions(memberPerms);
                return Object.entries(grouped).map(([category, perms]) => (
                  <div key={category} className="p-3 bg-muted rounded-lg">
                    <p className="font-medium mb-2">{getCategoryLabel(category)}</p>
                    <div className="space-y-2">
                      {perms.map((perm, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-green-600" />
                          <span className="text-sm">{getPermissionLabel(perm)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ));
              })()}
            </div>
          </ScrollArea>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPermissionsDialog(false)}>
              Kapat
            </Button>
            {canManage && selectedStaffMember?.id !== myPermissions?.user_id && (
              <Button onClick={() => {
                setShowPermissionsDialog(false);
                setEditingStaffId(selectedStaffMember.id);
                setSelectedRole(selectedStaffMember.admin_role);
              }}>
                <Edit className="w-4 h-4 mr-2" />
                Rolü Değiştir
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change User Role Dialog (Super Admin) - Enhanced with Custom Permissions */}
      <Dialog open={showChangeRoleDialog} onOpenChange={setShowChangeRoleDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-yellow-500" />
              Kullanıcı Rolü ve Yetkileri Düzenle
            </DialogTitle>
            <DialogDescription>
              {selectedUserForRole?.name} ({selectedUserForRole?.email}) kullanıcısının rolünü ve yetkilerini özelleştirin
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Current User Info */}
            <div className="p-4 bg-muted rounded-lg flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-primary font-bold text-lg">
                  {selectedUserForRole?.name?.charAt(0).toUpperCase() || 'U'}
                </span>
              </div>
              <div>
                <p className="font-semibold">{selectedUserForRole?.name || 'İsimsiz'}</p>
                <p className="text-sm text-muted-foreground">{selectedUserForRole?.email}</p>
                <div className="flex gap-2 mt-1">
                  <Badge variant="outline">
                    Mevcut: {selectedUserForRole?.role === 'admin' ? 'Admin' : 
                             selectedUserForRole?.role === 'seller' ? 'Satıcı' : 'Alıcı'}
                  </Badge>
                  {selectedUserForRole?.admin_role && (
                    <Badge variant="secondary">{selectedUserForRole?.admin_role}</Badge>
                  )}
                </div>
              </div>
            </div>

            {/* Main Role Selection */}
            <div className="space-y-2">
              <Label className="text-base font-semibold">1. Ana Rol Seçin</Label>
              <div className="grid grid-cols-3 gap-3">
                <button
                  type="button"
                  className={`p-4 rounded-lg border-2 transition-all ${
                    newUserRole === 'buyer' 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setNewUserRole('buyer')}
                >
                  <ShoppingCart className={`w-8 h-8 mx-auto mb-2 ${newUserRole === 'buyer' ? 'text-blue-600' : 'text-gray-400'}`} />
                  <p className="font-medium">Alıcı</p>
                  <p className="text-xs text-muted-foreground">Standart müşteri</p>
                </button>
                <button
                  type="button"
                  className={`p-4 rounded-lg border-2 transition-all ${
                    newUserRole === 'seller' 
                      ? 'border-green-500 bg-green-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setNewUserRole('seller')}
                >
                  <Store className={`w-8 h-8 mx-auto mb-2 ${newUserRole === 'seller' ? 'text-green-600' : 'text-gray-400'}`} />
                  <p className="font-medium">Satıcı</p>
                  <p className="text-xs text-muted-foreground">Ürün satabilir</p>
                </button>
                <button
                  type="button"
                  className={`p-4 rounded-lg border-2 transition-all ${
                    newUserRole === 'admin' 
                      ? 'border-purple-500 bg-purple-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setNewUserRole('admin')}
                >
                  <Shield className={`w-8 h-8 mx-auto mb-2 ${newUserRole === 'admin' ? 'text-purple-600' : 'text-gray-400'}`} />
                  <p className="font-medium">Admin</p>
                  <p className="text-xs text-muted-foreground">Yönetim paneli erişimi</p>
                </button>
              </div>
            </div>
            
            {/* Admin Sub-Role Selection */}
            {newUserRole === 'admin' && (
              <div className="space-y-3">
                <Label className="text-base font-semibold">2. Admin Alt Rolü Seçin</Label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: 'super_admin', name: 'Super Admin', desc: 'Tam yetki - tüm işlemler', icon: Crown, color: 'red' },
                    { key: 'admin', name: 'Admin', desc: 'Genel yönetim yetkileri', icon: Shield, color: 'purple' },
                    { key: 'moderator', name: 'Moderatör', desc: 'İçerik yönetimi', icon: Eye, color: 'blue' },
                    { key: 'support', name: 'Destek', desc: 'Müşteri desteği', icon: Users, color: 'green' },
                    { key: 'finance', name: 'Finans', desc: 'Finansal işlemler', icon: Lock, color: 'yellow' },
                    { key: 'custom', name: 'Özel Rol', desc: 'Yetkileri kendiniz seçin', icon: Settings, color: 'gray' },
                  ].map((role) => (
                    <button
                      key={role.key}
                      type="button"
                      className={`p-3 rounded-lg border-2 transition-all text-left ${
                        newUserAdminRole === role.key 
                          ? `border-${role.color}-500 bg-${role.color}-50` 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => setNewUserAdminRole(role.key)}
                    >
                      <div className="flex items-center gap-3">
                        <role.icon className={`w-5 h-5 ${newUserAdminRole === role.key ? `text-${role.color}-600` : 'text-gray-400'}`} />
                        <div>
                          <p className="font-medium">{role.name}</p>
                          <p className="text-xs text-muted-foreground">{role.desc}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
                
                {/* Custom Permissions Selection */}
                {newUserAdminRole === 'custom' && (
                  <div className="mt-4 p-4 border rounded-lg bg-gray-50">
                    <Label className="text-base font-semibold mb-3 block">3. Özel Yetkiler Seçin</Label>
                    <p className="text-sm text-muted-foreground mb-4">
                      Bu kullanıcıya vermek istediğiniz yetkileri işaretleyin
                    </p>
                    <ScrollArea className="h-[200px]">
                      <div className="space-y-4">
                        {Object.entries(groupPermissions(permissions)).map(([category, perms]) => (
                          <div key={category} className="space-y-2">
                            <p className="text-sm font-semibold text-gray-700 border-b pb-1">
                              {getCategoryLabel(category)}
                            </p>
                            <div className="grid grid-cols-2 gap-2">
                              {perms.map((perm) => (
                                <label 
                                  key={perm} 
                                  className="flex items-center gap-2 p-2 rounded hover:bg-white cursor-pointer"
                                >
                                  <Checkbox
                                    checked={customPermissions?.includes(perm)}
                                    onCheckedChange={(checked) => {
                                      if (checked) {
                                        setCustomPermissions(prev => [...(prev || []), perm]);
                                      } else {
                                        setCustomPermissions(prev => (prev || []).filter(p => p !== perm));
                                      }
                                    }}
                                  />
                                  <span className="text-sm">{getPermissionLabel(perm)}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                    {customPermissions?.length > 0 && (
                      <div className="mt-3 p-2 bg-blue-50 rounded">
                        <p className="text-xs text-blue-700">
                          Seçilen yetkiler: {customPermissions.length}
                        </p>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Role Preview */}
                {newUserAdminRole && newUserAdminRole !== 'custom' && (
                  <div className="mt-4 p-4 border rounded-lg bg-blue-50">
                    <p className="text-sm font-semibold text-blue-700 mb-2">Seçilen Rolün Yetkileri:</p>
                    <div className="flex flex-wrap gap-1">
                      {roles.find(r => r.key === newUserAdminRole)?.permissions?.includes('*') ? (
                        <Badge className="bg-red-100 text-red-700">Tam Yetki (Tüm İşlemler)</Badge>
                      ) : (
                        roles.find(r => r.key === newUserAdminRole)?.permissions?.slice(0, 8).map((perm, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs bg-white">
                            {getPermissionLabel(perm)}
                          </Badge>
                        ))
                      )}
                      {(roles.find(r => r.key === newUserAdminRole)?.permissions?.length || 0) > 8 && (
                        <Badge variant="secondary" className="text-xs">
                          +{roles.find(r => r.key === newUserAdminRole).permissions.length - 8} daha
                        </Badge>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                ⚠️ Rol değişikliği anında geçerli olacaktır. Kullanıcı bir sonraki girişinde yeni yetkilerle devam edecektir.
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowChangeRoleDialog(false)}>
              İptal
            </Button>
            <Button onClick={handleChangeUserRole} disabled={saving || !newUserRole}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <Save className="w-4 h-4 mr-2" />
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RoleManagementTab;
